import React from 'react'
import * as DialogPrimitive from '@grupoboticario/flora-react-dialog'
import { FloraVariants, forwardRef, styled } from '@grupoboticario/flora-react-system'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

export type ModalProps = DialogPrimitive.DialogProps

/**
 * @component
 * @name Modal
 * @description Modal component
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export const Modal = (props: ModalProps) => {
  return <DialogPrimitive.Dialog {...props} data-flora="modal" />
}

const StyledContent = styled(DialogPrimitive.DialogContent, {
  position: 'fixed',
  backgroundColor: '$backgroundPrimary',
  boxShadow: '$colors$shadowLight 0 10px 38px -10px, $colors$shadowDark 0 10px 20px -15px',
  borderRadius: '$large $large 0 0',
  boxSizing: 'border-box',
  zIndex: '$modal',

  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  bottom: 0,
  left: 0,
  height: 'fit-content',
  maxHeight: '100%',

  '@tablet': {
    height: 'fit-content',
    borderRadius: 0,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },

  variants: {
    size: {
      small: {
        '@tablet': {
          maxWidth: '28rem',
          minWidth: '28rem',
        },
      },
      standard: {
        '@tablet': {
          maxWidth: '48rem',
          minWidth: '48rem',
        },
      },
      large: {
        '@tablet': {
          maxWidth: '58rem',
          minWidth: '58rem',
        },
      },
    },
    isFull: {
      true: {
        maxWidth: '100%',
        height: '100%',
        borderRadius: 0,
        inset: 0,
        transform: 'translate(0, 0)',
      },
    },
  },
  defaultVariants: {
    size: 'standard',
  },
})
const StyledBody = styled(DialogPrimitive.DialogBody, {})
const StyledHeader = styled(DialogPrimitive.DialogHeader, {})
const StyledFooter = styled(DialogPrimitive.DialogFooter, {})
const StyledTrigger = styled(DialogPrimitive.DialogTrigger, {})
const StyledClose = styled(DialogPrimitive.DialogClose, {})

export type ModalContentProps = HTMLFloraProps<'div'> &
  FloraVariants<typeof StyledContent> &
  DialogPrimitive.DialogContentProps

/**
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export const ModalContent = forwardRef<'div', ModalContentProps>((props, forwardedRef) => {
  return <StyledContent {...props} data-flora="modal-content" ref={forwardedRef} />
})

export type ModalBodyProps = HTMLFloraProps<'div'> &
  FloraVariants<typeof DialogPrimitive.DialogBody>

/**
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export const ModalBody = forwardRef<'div', ModalBodyProps>((props, forwardedRef) => {
  return <StyledBody {...props} ref={forwardedRef} />
})

export type ModalHeaderProps = HTMLFloraProps<'header'> &
  FloraVariants<typeof DialogPrimitive.DialogHeader>

/**
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export const ModalHeader = forwardRef<'header', ModalHeaderProps>((props, forwardedRef) => {
  return <StyledHeader {...props} data-flora="modal-header" ref={forwardedRef} />
})

/**
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export type ModalFooterProps = HTMLFloraProps<'footer'> &
  FloraVariants<typeof DialogPrimitive.DialogFooter>

/**
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export const ModalFooter = forwardRef<'footer', ModalFooterProps>((props, forwardedRef) => {
  return <StyledFooter {...props} data-flora="modal-footer" ref={forwardedRef} />
})

export type ModalTriggerProps = HTMLFloraProps<'button'> &
  FloraVariants<typeof DialogPrimitive.DialogTrigger> &
  DialogPrimitive.DialogTriggerProps

/**
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export const ModalTrigger = forwardRef<'button', ModalTriggerProps>((props, forwardedRef) => {
  return <StyledTrigger {...props} asChild data-flora="modal-trigger" ref={forwardedRef} />
})

export type ModalCloseButtonProps = HTMLFloraProps<'button'> & DialogPrimitive.DialogCloseProps

/**
 * @tutorial https://flora.gb.tech/docs/components/core/modal
 */
export const ModalCloseButton = forwardRef<'button', ModalCloseButtonProps>(
  (props, forwardedRef) => {
    return <StyledClose {...props} data-flora="modal-close" ref={forwardedRef} />
  },
)
